import type { FC, MutableRefObject, ReactElement, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useUserProfile } from 'contexts/UserProfileContext';
import type {
  GetTicketsCountByRoofTopResponseDTO,
  GetTicketsRequestParamsDTO,
  GetTicketsResponseDTO,
} from 'declarations/dto/tickets';
import type { SortingOrderE } from 'declarations/enums/common';
import { RoutePathsE } from 'declarations/enums/routes';
import { FILTER_PREFERENCES_VERSION, StorageE } from 'declarations/enums/storage';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import type {
  AgeFilterValuesE,
  ParameterSortingValuesE,
  ScheduleFilterValuesE,
  StatusFilterValuesE,
} from 'declarations/enums/tickets';
import { TicketsRefinementKeysE, TicketsViewModeE } from 'declarations/enums/tickets';
import type { RoofTopI } from 'declarations/interfaces/roofTop';
import type { TicketCountByRoofTopI } from 'declarations/interfaces/tickets';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { getCurrentUserId } from 'helpers/user';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import { useFetchTickets, useFetchTicketsCountByRoofTop } from 'hooks/api/useTickets';

import { useResponsive } from './ResponsiveContext';

export type TicketsContextT = {
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
  ticketsCount: number;
  ticketsCountByRoofTop: GetTicketsCountByRoofTopResponseDTO | null | undefined;
  mappedRooftopsList: TicketCountByRoofTopI[] | null | undefined;
  isTicketsFetchingError: boolean;
  fetchTicketsErrorMessage: string | undefined;
  fetchTicketsValidationErrorMessage: string | undefined;
  isTicketsFetchingCountByRoofTopError: boolean;
  isSuccessfullyFetchedTickets: boolean;
  isLoadingTickets: boolean;
  isLoadingTicketsCountByRoofTop: boolean;
  setSidebarIsOpen: (isSidebarOpened: boolean) => void;
  isSidebarOpened: boolean;
  setTicketsViewMode: (ticketsViewMode: TicketsViewModeE) => void;
  ticketsViewMode: TicketsViewModeE;
  setTicketId: (id: string | null) => void;
  selectedTicketId: string | null;
  isNewTicketPopupOpened: boolean;
  setNewTicketPopupVisibility: (isOpened: boolean) => void;
  isROListPopupOpened: boolean;
  setROListPopupVisibility: (isOpened: boolean) => void;
  getSearchParam: (key: TicketsRefinementKeysE) => string | null;
  updateSearchParam: (key: TicketsRefinementKeysE, value: string) => void;
  clearSearchParam: (keys: TicketsRefinementKeysE | TicketsRefinementKeysE[]) => void;
  isFetchingNextTicketsPage: boolean;
  isFetchingTickets: boolean;
  hasNextTicketsPage: boolean | undefined;
  fetchNextTicketsPage: () => void;
  fetchTicketsParams: GetTicketsRequestParamsDTO;
  activeScheduleCards: string[];
  setActiveScheduleCards: (cards: string[]) => void;
  activeScheduleFilters: ScheduleFilterValuesE[];
  setActiveScheduleFilters: (filters: ScheduleFilterValuesE[]) => void;
  getAllSearchParam: (key: TicketsRefinementKeysE) => number[];
  updateSearchParamsArray: (
    key: TicketsRefinementKeysE,
    value: (number | string | ScheduleFilterValuesE)[] | null
  ) => void;
  selectedRoofTop: string;
  scheduleCategoryId: number;
  setScheduleCategoryId: (id: number) => void;
  dealerSelectedRoofTopCount: number;
  setDealerSelectedRoofTopCount: (count: number) => void;
  dealerSelectedRooftopIds: string | null;
  setDealerSelectedRooftopIds: (id: string | null) => void;
  initialSelectedRooftopIds: string[];
  setInitialSelectedRooftopIds: (ids: string[]) => void;
  roofTopsList: RoofTopI[] | null;
  shouldLoadMoreRooftops: boolean;
  hasNextRooftopsPage: boolean;
  fetchNextRooftopsPage: () => void;
  selectedTicketsIds: string[];
  setSelectedTicketsIds: (id: string[]) => void;
  activeTab: ActiveTabTypeE;
  setActiveTab: (tab: ActiveTabTypeE) => void;
  isScheduleTicketList: boolean;
  setIsScheduleTicketList: (value: boolean) => void;
  ticketsTotalCount: number | undefined;
  openedTicketTabs: Record<string, string>;
  setOpenedTicketTab: (tabLabel: string, ticketId: string) => void;
  closeTicketTab: (tabLabel: string) => void;
  openedScheduleTicketTabs: Record<string, string>;
  setOpenedScheduleTicketTab: (tabLabel: string, ticketId: string) => void;
  closeScheduleTicketTab: (tabLabel: string) => void;
  ticketListScrollPosition: number;
  setTicketListScrollPosition: (position: number) => void;
  ticketListScrollContainerRef: MutableRefObject<HTMLDivElement | null>;
  saveTicketListScrollPosition: () => void;
};

type TicketsProviderPropsT = { children: ReactNode };

const contextDefaultValue = {
  ticketsPages: null,
  ticketsCount: 0,
  ticketsCountByRoofTop: null,
  mappedRooftopsList: null,
  isTicketsFetchingError: false,
  fetchTicketsErrorMessage: undefined,
  fetchTicketsValidationErrorMessage: undefined,
  isTicketsFetchingCountByRoofTopError: false,
  isSuccessfullyFetchedTickets: false,
  isLoadingTickets: false,
  isLoadingTicketsCountByRoofTop: false,
  setSidebarIsOpen: (): void => undefined,
  isSidebarOpened: true,
  setTicketsViewMode: (): void => undefined,
  ticketsViewMode: TicketsViewModeE.List,
  setTicketId: (): void => undefined,
  selectedTicketId: '',
  isNewTicketPopupOpened: false,
  setNewTicketPopupVisibility: (): void => undefined,
  isROListPopupOpened: false,
  setROListPopupVisibility: (): void => undefined,
  getSearchParam: (): string | null => null,
  updateSearchParam: (): void => undefined,
  clearSearchParam: (): void => undefined,
  isFetchingNextTicketsPage: false,
  isFetchingTickets: false,
  hasNextTicketsPage: false,
  fetchNextTicketsPage: (): void => undefined,
  fetchTicketsParams: {},
  activeScheduleCards: [],
  setActiveScheduleCards: (): void => undefined,
  activeScheduleFilters: [],
  setActiveScheduleFilters: (): void => undefined,
  getAllSearchParam: (): number[] => [],
  updateSearchParamsArray: (): void => undefined,
  selectedRoofTop: '',
  scheduleCategoryId: 0,
  setScheduleCategoryId: (): void => undefined,
  dealerSelectedRoofTopCount: 0,
  setDealerSelectedRoofTopCount: (): void => undefined,
  dealerSelectedRooftopIds: '',
  setDealerSelectedRooftopIds: (): void => undefined,
  initialSelectedRooftopIds: [],
  setInitialSelectedRooftopIds: (): void => undefined,
  roofTopsList: null,
  shouldLoadMoreRooftops: false,
  hasNextRooftopsPage: false,
  fetchNextRooftopsPage: (): void => undefined,
  selectedTicketsIds: [],
  setSelectedTicketsIds: (): void => undefined,
  activeTab: ActiveTabTypeE.Tickets,
  setActiveTab: (): void => undefined,
  isScheduleTicketList: false,
  setIsScheduleTicketList: (): void => undefined,
  ticketsTotalCount: 0,
  openedTicketTabs: {},
  setOpenedTicketTab: (): void => undefined,
  closeTicketTab: (): void => undefined,
  openedScheduleTicketTabs: {},
  setOpenedScheduleTicketTab: (): void => undefined,
  closeScheduleTicketTab: (): void => undefined,
  ticketListScrollPosition: 0,
  setTicketListScrollPosition: (): void => undefined,
  ticketListScrollContainerRef: { current: null } as MutableRefObject<HTMLDivElement | null>,
  saveTicketListScrollPosition: (): void => undefined,
};

export const TicketsContext = createContext<TicketsContextT>(contextDefaultValue);

const getInitialOpenedTicketTabs = (): Record<string, string> => {
  try {
    const stored = localStorage.getItem(StorageE.TicketTabs);
    return stored ? (JSON.parse(stored) as Record<string, string>) : {};
  } catch {
    return {};
  }
};

const getInitialOpenedScheduleTicketTabs = (): Record<string, string> => {
  try {
    const stored = localStorage.getItem(StorageE.ScheduleTicketTabs);
    return stored ? (JSON.parse(stored) as Record<string, string>) : {};
  } catch {
    return {};
  }
};

const TicketsProvider: FC<TicketsProviderPropsT> = ({ children }): ReactElement => {
  /**
   * Hooks
   */
  const [ticketsViewMode, setTicketsViewMode] = useState<TicketsViewModeE>(TicketsViewModeE.List);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [openedTicketTabs, setOpenedTicketTabsState] = useState<Record<string, string>>(
    getInitialOpenedTicketTabs
  );
  const [openedScheduleTicketTabs, setOpenedScheduleTicketTabsState] = useState<
    Record<string, string>
  >(getInitialOpenedScheduleTicketTabs);
  const [isNewTicketPopupOpened, setNewTicketPopupVisibility] = useState<boolean>(false);
  const [isROListPopupOpened, setROListPopupVisibility] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRoofTop, setSelectedRoofTop] = useState<string>('');
  const navigate = useNavigate();
  const [activeScheduleCards, setActiveScheduleCards] = useState<string[]>([]);
  const [activeScheduleFilters, setActiveScheduleFilters] = useState<ScheduleFilterValuesE[]>([]);
  const [defaultRooftopId, setDefaultRooftopId] = useState<string>();
  const [scheduleCategoryId, setScheduleCategoryId] = useState<number>(0);
  const [dealerSelectedRoofTopCount, setDealerSelectedRoofTopCount] = useState(0);
  const [dealerSelectedRooftopIds, setDealerSelectedRooftopIdsState] = useState<string | null>(
    () => {
      const userId = getCurrentUserId();
      if (!userId) return '';
      try {
        const stored = localStorage.getItem(`${StorageE.DealerCardSelectedRooftopIds}${userId}`);
        return stored != null && stored !== '' ? stored : '';
      } catch {
        return '';
      }
    }
  );

  const setDealerSelectedRooftopIds = useCallback((id: string | null): void => {
    setDealerSelectedRooftopIdsState(id);
  }, []);
  const [initialSelectedRooftopIds, setInitialSelectedRooftopIds] = useState<string[]>([]);
  const [selectedTicketsIds, setSelectedTicketsIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTabTypeE>(
    (sessionStorage.getItem('activeTab') as ActiveTabTypeE) || ActiveTabTypeE.Tickets
  );
  const [isScheduleTicketList, setIsScheduleTicketList] = useState(false);
  const [ticketListScrollPosition, setTicketListScrollPosition] = useState(0);
  const ticketListScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const saveTicketListScrollPosition = useCallback((): void => {
    const el = ticketListScrollContainerRef.current;
    if (el) setTicketListScrollPosition(el.scrollTop);
  }, []);

  /**
   * Variables
   */
  const { isMobile } = useResponsive();
  const [isSidebarOpened, setSidebarIsOpen] = useState(true);
  const currentPage = 1;
  const ROOFTOPS_PAGE_SIZE = 25;
  const ticketsPerPage = 25;
  const location = useLocation();
  const hasRestoredFilterPreferencesRef = useRef(false);

  /** Filter keys to persist (excludes RONumber, SearchTerm, sorting, paging) */
  const FILTER_PREFERENCE_KEYS: TicketsRefinementKeysE[] = useMemo(
    (): TicketsRefinementKeysE[] => [
      TicketsRefinementKeysE.Status,
      TicketsRefinementKeysE.AssignedToMe,
      TicketsRefinementKeysE.TicketAge,
      TicketsRefinementKeysE.PastMonth,
      TicketsRefinementKeysE.Past30Days,
      TicketsRefinementKeysE.IsArchived,
      TicketsRefinementKeysE.AgentRoofTopRole,
      TicketsRefinementKeysE.Assignee,
      TicketsRefinementKeysE.Category,
      TicketsRefinementKeysE.SubCategory,
      TicketsRefinementKeysE.Schedule,
      TicketsRefinementKeysE.DMSScheduleAccounts,
      TicketsRefinementKeysE.RoofTopId,
      TicketsRefinementKeysE.ScheduleFileId,
    ],
    []
  );

  const categoryParamForApi = useMemo((): string | undefined => {
    const raw = searchParams.get(TicketsRefinementKeysE.Category);
    if (activeTab === ActiveTabTypeE.Schedule) {
      if (scheduleCategoryId) {
        return String(scheduleCategoryId);
      }
      return raw || undefined;
    }
    if (!raw) return undefined;
    const ids = raw
      .split(',')
      .map((c: string): number => Number(c.trim()))
      .filter((n: number): boolean => !Number.isNaN(n));
    const hasValidScheduleId = Number.isFinite(scheduleCategoryId) && scheduleCategoryId > 0;
    const filtered = hasValidScheduleId
      ? ids.filter((id: number): boolean => id !== scheduleCategoryId)
      : ids;
    return filtered.length > 0 ? filtered.join(',') : undefined;
  }, [searchParams, activeTab, scheduleCategoryId]);

  const fetchTicketsParams = useMemo((): GetTicketsRequestParamsDTO => {
    const roofTopIdForRequest =
      (searchParams.get(TicketsRefinementKeysE.RoofTopId) as string) || undefined;
    return {
      [TicketsRefinementKeysE.RoofTopId]: roofTopIdForRequest,
      [TicketsRefinementKeysE.Status]:
        (searchParams.get(TicketsRefinementKeysE.Status) as StatusFilterValuesE) || undefined,
      [TicketsRefinementKeysE.TicketAge]:
        (searchParams.get(TicketsRefinementKeysE.TicketAge) as AgeFilterValuesE) || undefined,
      [TicketsRefinementKeysE.RONumber]:
        searchParams.get(TicketsRefinementKeysE.RONumber) || undefined,
      [TicketsRefinementKeysE.SearchTerm]:
        searchParams.get(TicketsRefinementKeysE.SearchTerm) || undefined,
      [TicketsRefinementKeysE.AssignedToMe]:
        !!searchParams.get(TicketsRefinementKeysE.AssignedToMe) || undefined,
      [TicketsRefinementKeysE.PastMonth]:
        !!searchParams.get(TicketsRefinementKeysE.PastMonth) || undefined,
      [TicketsRefinementKeysE.Past30Days]:
        !!searchParams.get(TicketsRefinementKeysE.Past30Days) || undefined,
      [TicketsRefinementKeysE.IsArchived]:
        !!searchParams.get(TicketsRefinementKeysE.IsArchived) || false,
      [TicketsRefinementKeysE.Parameter]:
        (searchParams.get(TicketsRefinementKeysE.Parameter) as ParameterSortingValuesE) ||
        undefined,
      [TicketsRefinementKeysE.Order]:
        (searchParams.get(TicketsRefinementKeysE.Order) as SortingOrderE) || undefined,
      [TicketsRefinementKeysE.CurrentPage]:
        (searchParams.get(TicketsRefinementKeysE.CurrentPage) as string) || currentPage.toString(),
      [TicketsRefinementKeysE.CountPerPage]:
        (searchParams.get(TicketsRefinementKeysE.CountPerPage) as string) ||
        ticketsPerPage.toString(),
      [TicketsRefinementKeysE.Schedule]:
        activeTab === ActiveTabTypeE.Schedule
          ? (searchParams.get(TicketsRefinementKeysE.Schedule) as ScheduleFilterValuesE) ||
            undefined
          : undefined,
      [TicketsRefinementKeysE.Category]: categoryParamForApi,
      [TicketsRefinementKeysE.SubCategory]:
        activeTab === ActiveTabTypeE.Schedule
          ? searchParams.get(TicketsRefinementKeysE.SubCategory) || undefined
          : undefined,
      [TicketsRefinementKeysE.RequestSubCategoryIds]:
        activeTab !== ActiveTabTypeE.Schedule
          ? searchParams.get(TicketsRefinementKeysE.RequestSubCategoryIds) || undefined
          : undefined,
      [TicketsRefinementKeysE.Assignee]:
        searchParams.get(TicketsRefinementKeysE.Assignee) || undefined,
    };
  }, [searchParams, activeTab, categoryParamForApi, currentPage, ticketsPerPage]);

  const getSearchParam = useCallback(
    (key: TicketsRefinementKeysE): string | null => searchParams.get(key),
    [searchParams]
  );
  const fetchCountByRooftopsParams = useMemo(
    (): { paging: { currentPage: number; countPerPage: number }; roofTopIds: string | null } => ({
      paging: {
        currentPage: currentPage,
        countPerPage: ROOFTOPS_PAGE_SIZE,
      },
      roofTopIds: dealerSelectedRooftopIds,
    }),
    [currentPage, ROOFTOPS_PAGE_SIZE, dealerSelectedRooftopIds]
  );
  /**
   * Api hooks
   */
  const { userProfile } = useUserProfile();
  const ticketsResult = useFetchTickets(fetchTicketsParams);
  const ticketsCountByRoofTopResult = useFetchTicketsCountByRoofTop(fetchCountByRooftopsParams);
  const categoriesResult = useFetchTicketRequestCategories(
    true,
    selectedRoofTop ?? defaultRooftopId
  );
  /**
   * Variables
   */
  const fetchTicketsErrorMessage = useMemo(
    (): string | undefined => getErrorMessage(ticketsResult.error),
    [ticketsResult.error]
  );
  const fetchTicketsValidationErrorMessage = useMemo(
    (): string | undefined => getValidationErrorMessage(ticketsResult.error),
    [ticketsResult.error]
  );

  /**
   * Handlers
   */
  const getAllSearchParam = useCallback(
    (key: TicketsRefinementKeysE): number[] => searchParams.getAll(key).map(Number),
    [searchParams]
  );

  const updateSearchParam = useCallback(
    (key: TicketsRefinementKeysE, value: string): void => {
      searchParams.set(key, value);
      setSearchParams(searchParams);
      if (isMobile) {
        setSidebarIsOpen(false);
      }
    },
    [isMobile, searchParams, setSearchParams, setSidebarIsOpen]
  );
  const clearSearchParam = useCallback(
    (keys: TicketsRefinementKeysE | TicketsRefinementKeysE[]): void => {
      const next = new URLSearchParams(searchParams);
      const keysToDelete = Array.isArray(keys) ? keys : [keys];
      keysToDelete.forEach((key): void => next.delete(key));
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const updateSearchParamsArray = useCallback(
    (
      key: TicketsRefinementKeysE,
      valuesArray: (number | string | ScheduleFilterValuesE)[] | null
    ): void => {
      let queryString = window.location.pathname + '?';
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach((value, paramKey): void => {
        if (paramKey !== key) {
          queryString += `${paramKey}=${encodeURIComponent(value)}&`;
        }
      });

      if (valuesArray && valuesArray.length > 0) {
        const valuesString = valuesArray.join(',');
        queryString += `${key}=${encodeURIComponent(valuesString)}`;
      } else {
        queryString = queryString.replace(/&$/, '');
      }
      navigate(queryString, { replace: true });
    },
    [navigate]
  );

  /**
   * Effects
   */
  useEffect((): void => {
    setSelectedRoofTop(getSearchParam(TicketsRefinementKeysE.RoofTopId) ?? '');
  }, [getSearchParam]);

  useEffect((): void => {
    if (ticketsCountByRoofTopResult.data && ticketsCountByRoofTopResult.data.pages.length > 0) {
      const countByRoofTops = ticketsCountByRoofTopResult.data.pages.flatMap(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- infinite query page type varies by response structure
        (page: any) => page?.countByRoofTop || []
      );

      if (countByRoofTops.length > 0) {
        setDefaultRooftopId(countByRoofTops[0]?.roofTopId);
      } else {
        setDefaultRooftopId(undefined);
      }
    }
  }, [ticketsCountByRoofTopResult.data]);

  useEffect((): void => {
    if (!defaultRooftopId) return;

    categoriesResult.refetch().then(({ data }): void => {
      const scheduleCategory = data?.find(
        (category): boolean => category.categoryName.toLowerCase() === 'schedule'
      );

      if (!scheduleCategory) return;

      setScheduleCategoryId(scheduleCategory.id);

      const rawCategoryParam = getSearchParam(TicketsRefinementKeysE.Category);
      if (!rawCategoryParam) return;

      const categoryIds = rawCategoryParam.split(',').map((id): number => Number(id.trim()));

      const hasInvalidCategory = categoryIds.some((id): boolean => isNaN(id) || id === 0);

      if (hasInvalidCategory) {
        updateSearchParamsArray(TicketsRefinementKeysE.Category, [scheduleCategory.id]);
      }
    });
  }, [defaultRooftopId, categoriesResult, getSearchParam, updateSearchParamsArray]);

  useEffect((): void => {
    if (activeTab === ActiveTabTypeE.Schedule) {
      const rawCategoryParam = searchParams.get(TicketsRefinementKeysE.Category);
      if (rawCategoryParam) {
        updateSearchParamsArray(TicketsRefinementKeysE.Category, null);
      }
    }
  }, [activeTab, searchParams, updateSearchParamsArray]);

  useEffect((): void => {
    if (scheduleCategoryId === 0) return;
    if (activeTab !== ActiveTabTypeE.Schedule) {
      const rawCategoryParam = searchParams.get(TicketsRefinementKeysE.Category);
      const categoryIds = rawCategoryParam
        ? rawCategoryParam
            .split(',')
            .map((id): number => Number(id.trim()))
            .filter((id): boolean => !isNaN(id))
        : [];
      const hasOnlyScheduleCategory =
        categoryIds.length === 1 && categoryIds[0] === scheduleCategoryId;
      if (hasOnlyScheduleCategory) {
        updateSearchParamsArray(TicketsRefinementKeysE.Category, null);
      }
    }
  }, [activeTab, scheduleCategoryId, searchParams, updateSearchParamsArray]);

  /** Restore filter preferences from localStorage on initial load */
  useEffect((): void => {
    const pathname = location.pathname;
    const isTicketsList =
      pathname === RoutePathsE.TICKETS_PAGE || pathname === `${RoutePathsE.TICKETS_PAGE}/`;
    if (!isTicketsList || hasRestoredFilterPreferencesRef.current) return;

    const hasAnyFilterParam = FILTER_PREFERENCE_KEYS.some(
      (key): boolean => searchParams.get(key) !== null
    );
    if (hasAnyFilterParam) {
      hasRestoredFilterPreferencesRef.current = true;
      return;
    }

    const userId = getCurrentUserId();
    if (!userId) return;

    try {
      const stored = localStorage.getItem(`${StorageE.TicketFilterPreferences}${userId}`);
      if (!stored) return;

      const parsed = JSON.parse(stored) as { version?: number; params?: Record<string, string> };
      if (parsed.version !== FILTER_PREFERENCES_VERSION || !parsed.params) return;

      const next = new URLSearchParams(window.location.search);
      Object.entries(parsed.params).forEach(([key, value]): void => {
        if (value != null && value !== '') next.set(key, value);
      });
      setSearchParams(next);
    } catch {
      // Ignore invalid stored data
    }
    hasRestoredFilterPreferencesRef.current = true;
  }, [FILTER_PREFERENCE_KEYS, location.pathname, searchParams, setSearchParams]);

  /** Persist filter preferences when they change */
  useEffect((): void => {
    const userId = getCurrentUserId();
    if (!userId) return;

    try {
      const params: Record<string, string> = {};
      FILTER_PREFERENCE_KEYS.forEach((key): void => {
        const value = searchParams.get(key);
        if (value != null && value !== '') params[key] = value;
      });
      const payload = { version: FILTER_PREFERENCES_VERSION, params };
      localStorage.setItem(`${StorageE.TicketFilterPreferences}${userId}`, JSON.stringify(payload));
    } catch {
      // Ignore storage errors
    }
  }, [FILTER_PREFERENCE_KEYS, searchParams]);

  /** Persist dealer selection to localStorage (empty = all) */
  useEffect((): void => {
    const userId = getCurrentUserId();
    if (!userId) return;

    try {
      const value = dealerSelectedRooftopIds ?? '';
      localStorage.setItem(`${StorageE.DealerCardSelectedRooftopIds}${userId}`, value);
    } catch {
      // Ignore storage errors
    }
  }, [dealerSelectedRooftopIds]);

  const combinedTicketsCountByRoofTop = useMemo((): TicketCountByRoofTopI[] => {
    if (ticketsCountByRoofTopResult.data?.pages) {
      return ticketsCountByRoofTopResult.data.pages.reduce<TicketCountByRoofTopI[]>(
        (acc, page): TicketCountByRoofTopI[] => {
          if (
            page?.countByRoofTop &&
            Array.isArray(page.countByRoofTop) &&
            page.countByRoofTop.length > 0
          ) {
            return acc.concat(page.countByRoofTop);
          }
          return acc;
        },
        []
      );
    }
    return [];
  }, [ticketsCountByRoofTopResult.data]);

  useEffect((): void => {
    localStorage.setItem(StorageE.TicketTabs, JSON.stringify(openedTicketTabs));
  }, [openedTicketTabs]);

  useEffect((): void => {
    localStorage.setItem(StorageE.ScheduleTicketTabs, JSON.stringify(openedScheduleTicketTabs));
  }, [openedScheduleTicketTabs]);

  const setOpenedTicketTab = useCallback((tabLabel: string, ticketIdToAdd: string): void => {
    setOpenedTicketTabsState(prev => ({ ...prev, [tabLabel]: ticketIdToAdd }));
  }, []);

  const closeTicketTab = useCallback((tabLabel: string): void => {
    setOpenedTicketTabsState(prev => {
      const next = { ...prev };
      delete next[tabLabel];
      return next;
    });
  }, []);

  const setOpenedScheduleTicketTab = useCallback(
    (tabLabel: string, ticketIdToAdd: string): void => {
      setOpenedScheduleTicketTabsState(prev => ({ ...prev, [tabLabel]: ticketIdToAdd }));
    },
    []
  );

  const closeScheduleTicketTab = useCallback((tabLabel: string): void => {
    setOpenedScheduleTicketTabsState(prev => {
      const next = { ...prev };
      delete next[tabLabel];
      return next;
    });
  }, []);

  const ticketsTotalCountForTab = useMemo((): number => {
    const selectedRoofTopId = searchParams.get(TicketsRefinementKeysE.RoofTopId);
    if (selectedRoofTopId) {
      const rooftop = combinedTicketsCountByRoofTop.find(
        (r): boolean => r.roofTopId === selectedRoofTopId
      );
      return rooftop?.total ?? 0;
    }
    const firstPage = ticketsCountByRoofTopResult.data?.pages?.[0];
    return (firstPage as { total?: number } | undefined)?.total ?? 0;
  }, [searchParams, combinedTicketsCountByRoofTop, ticketsCountByRoofTopResult.data?.pages]);

  return (
    <TicketsContext.Provider
      value={{
        ticketsPages: ticketsResult.data?.pages ?? null,
        ticketsCount: ticketsResult.data?.pages[0].count || 0,
        isTicketsFetchingError: ticketsResult.isError,
        fetchTicketsErrorMessage,
        fetchTicketsValidationErrorMessage,
        mappedRooftopsList: combinedTicketsCountByRoofTop,
        isLoadingTickets: ticketsResult.isLoading || ticketsResult.isRefetching,
        isSuccessfullyFetchedTickets: ticketsResult.isSuccess,
        ticketsCountByRoofTop: ticketsCountByRoofTopResult.data?.pages[0],
        isTicketsFetchingCountByRoofTopError: ticketsCountByRoofTopResult.isError,
        isLoadingTicketsCountByRoofTop:
          ticketsCountByRoofTopResult.isLoading || ticketsCountByRoofTopResult.isFetching,
        setSidebarIsOpen,
        isSidebarOpened: isSidebarOpened,
        setTicketsViewMode,
        ticketsViewMode,
        setTicketId,
        selectedTicketId: ticketId,
        isNewTicketPopupOpened,
        setNewTicketPopupVisibility,
        isROListPopupOpened,
        setROListPopupVisibility,
        getSearchParam,
        updateSearchParam,
        getAllSearchParam,
        updateSearchParamsArray,
        clearSearchParam,
        isFetchingNextTicketsPage: ticketsResult.isFetchingNextPage,
        isFetchingTickets: ticketsResult.isFetching,
        fetchNextTicketsPage: ticketsResult.fetchNextPage,
        hasNextTicketsPage: ticketsResult.hasNextPage,
        fetchTicketsParams,
        activeScheduleCards,
        setActiveScheduleCards,
        activeScheduleFilters,
        setActiveScheduleFilters,
        selectedRoofTop,
        scheduleCategoryId,
        setScheduleCategoryId,
        dealerSelectedRoofTopCount,
        setDealerSelectedRoofTopCount,
        setDealerSelectedRooftopIds,
        dealerSelectedRooftopIds,
        initialSelectedRooftopIds,
        setInitialSelectedRooftopIds,
        roofTopsList: userProfile?.mappedRoofTops ?? null,
        hasNextRooftopsPage: Boolean(ticketsCountByRoofTopResult.hasNextPage),
        fetchNextRooftopsPage: ticketsCountByRoofTopResult.fetchNextPage,
        shouldLoadMoreRooftops: Boolean(
          ticketsCountByRoofTopResult.isRefetching &&
            ticketsCountByRoofTopResult.isFetchingNextPage &&
            ticketsCountByRoofTopResult.hasNextPage
        ),
        selectedTicketsIds,
        setSelectedTicketsIds,
        activeTab,
        setActiveTab,
        isScheduleTicketList,
        setIsScheduleTicketList,
        ticketsTotalCount: ticketsTotalCountForTab,
        openedTicketTabs,
        setOpenedTicketTab,
        closeTicketTab,
        openedScheduleTicketTabs,
        setOpenedScheduleTicketTab,
        closeScheduleTicketTab,
        ticketListScrollPosition,
        setTicketListScrollPosition,
        ticketListScrollContainerRef,
        saveTicketListScrollPosition,
      }}
    >
      {children}
    </TicketsContext.Provider>
  );
};

const useTickets = (): TicketsContextT => useContext(TicketsContext);

export { TicketsProvider, useTickets };
